import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Optional,
  Output,
  Self,
} from "@angular/core";
import { NgControl } from "@angular/forms";

/**
 * Normalizza un codice letto da scanner o digitato a mano:
 * rimuove i caratteri di controllo (CR, LF, TAB, GS...) che alcuni lettori aggiungono,
 * toglie gli spazi esterni e porta in maiuscolo.
 */
export function normalizeScanCode(value: string | null | undefined): string {
  return (value || "")
    .replace(/[\x00-\x1F\x7F]/g, "")
    .trim()
    .toUpperCase();
}

/**
 * Input compatibile con lettori barcode in modalità "tastiera" (keyboard wedge).
 *
 * - al focus seleziona il contenuto, così una nuova scansione sostituisce il valore precedente;
 * - intercetta l'Invio finale dello scanner (niente submit accidentali) e normalizza il valore;
 * - normalizza anche al blur, per i valori digitati a mano;
 * - emette `scanDetected` = true se i caratteri sono arrivati a raffica (< 40 ms l'uno dall'altro),
 *   false appena l'utente modifica a mano il campo.
 *
 * Uso: <input type="text" appScanInput [(ngModel)]="codice" (scanDetected)="letto = $event" />
 */
@Directive({
  selector: "input[appScanInput]",
  standalone: true,
})
export class ScanInputDirective {
  @Output() scanDetected = new EventEmitter<boolean>();

  /** Distanza massima (ms) tra due caratteri per considerarli inviati da uno scanner. */
  private static readonly SCAN_KEY_GAP_MS = 40;
  /** Numero minimo di caratteri "veloci" consecutivi per considerare la lettura uno scan. */
  private static readonly SCAN_MIN_FAST_KEYS = 3;

  private lastKeyAt = 0;
  private fastKeys = 0;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    @Optional() @Self() private ngControl: NgControl | null,
  ) {}

  @HostListener("focus")
  onFocus(): void {
    this.el.nativeElement.select();
  }

  @HostListener("keydown", ["$event"])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      event.preventDefault();
      const scanned = this.fastKeys >= ScanInputDirective.SCAN_MIN_FAST_KEYS;
      this.fastKeys = 0;
      const value = this.applyNormalizedValue();
      this.scanDetected.emit(scanned && !!value);
      return;
    }

    // Ignoro tasti non stampabili (Shift, frecce, ecc.)
    if (event.key.length !== 1) return;

    const now = performance.now();
    const gap = now - this.lastKeyAt;
    this.lastKeyAt = now;
    this.fastKeys = gap < ScanInputDirective.SCAN_KEY_GAP_MS ? this.fastKeys + 1 : 0;
  }

  @HostListener("input")
  onInput(): void {
    this.scanDetected.emit(false);
  }

  @HostListener("blur")
  onBlur(): void {
    this.applyNormalizedValue();
  }

  private applyNormalizedValue(): string {
    const normalized = normalizeScanCode(this.el.nativeElement.value);
    const control = this.ngControl?.control;
    if (control) {
      if (control.value !== normalized) {
        control.setValue(normalized);
        // con [(ngModel)] serve anche aggiornare la proprietà del componente
        this.ngControl!.viewToModelUpdate(normalized);
      }
    } else if (this.el.nativeElement.value !== normalized) {
      this.el.nativeElement.value = normalized;
    }
    return normalized;
  }
}
